import { useCallback, useEffect, useRef, useState } from "react"
import moment from "moment"
import ProctoringApi from "services/apis/proctoring"
import { storage } from "services/config/storage"
import { useGameplayConfig } from "store"
import { PERMISSION_ERRORS } from "constants/globalConstants"
import { classifyMediaDeviceError } from "utils/helper"
import { HIDE_GAME_IFRAME_EVENT } from "constants/helperModalConstants"

export const useVideoRecorder = () => {
    const [recorder, setRecorder] = useState(null)
    const [recorderState, setRecorderState] = useState("")
    const [isPermissionModalVisible, setIsPermissionModalVisible] = useState("");
    const apiFailureCountRef = useRef(0)

    const { isVideoProctored } = useGameplayConfig();
    const mimeType = MediaRecorder.isTypeSupported("video/webm") ? "video/webm" : "video/mp4"
    const fileExtension = mimeType.split('/')[1]

    useEffect(() => {
        if (isPermissionModalVisible) window.dispatchEvent(new Event(HIDE_GAME_IFRAME_EVENT))
    }, [isPermissionModalVisible])

    useEffect(() => {
        (async () => {
            try {
                if (isVideoProctored) {
                    const videoDeviceId = storage.get.webCam()
                    const audioDeviceId = storage.get.micDevice()

                    RECORDING_CONST.stream = await navigator.mediaDevices.getUserMedia({
                        video: {
                            // Safari rejects a mandatory 5 FPS maximum when the
                            // camera cannot expose a mode at or below that rate.
                            // Keep it as a bandwidth-saving preference instead.
                            frameRate: { ideal: 5 },
                            ...(videoDeviceId ? { deviceId: { ideal: videoDeviceId } } : {}),
                        },
                        audio: audioDeviceId
                            ? { deviceId: { ideal: audioDeviceId } }
                            : true,
                    })
                    const mediaRecorder = new MediaRecorder(RECORDING_CONST.stream, {
                        mimeType,
                        videoBitsPerSecond: RECORDING_CONST.videoBitsPerSecond,
                        audioBitsPerSecond: RECORDING_CONST.audioBitsPerSecond
                    })
                    setRecorder(mediaRecorder)
                }
            } catch (err) {
                console.error("Error in camera permission for video", err)
                const classifiedError = classifyMediaDeviceError(err?.toString())
                setIsPermissionModalVisible(classifiedError)
            }
        })()
        return () => {
            stopRecording()
            setRecorder(null)
            RECORDING_CONST.stream = null
        }
    }, [isVideoProctored])

    useEffect(() => {
        if (recorder) {
            startRecording()
            recorder.addEventListener("dataavailable", handleDataAvailable)
        }
        return () => recorder?.removeEventListener("dataavailable", handleDataAvailable)
    }, [recorder])

    useEffect(() => {
        let timeout;
        try {
            if (recorderState === "recording") {
                timeout = setTimeout(() => {
                    recorder?.stop()
                    setRecorderState(recorder?.state)
                }, RECORDING_CONST.recordingInterval)
            } else if (recorderState === "inactive") {
                recorder?.start()
                setRecorderState(recorder?.state)
            }
        } catch {
            setIsPermissionModalVisible(true)
        }

        return () => clearTimeout(timeout)
    }, [recorderState])

    const handleDataAvailable = useCallback(async (event) => {
        const currentTime = new Date()
        /**
         * TODO :: BE support for webm
         * 
         * Save fileName as mp4 - Regardless mimeType
         * Webm not supported yet
         *  */

        // Post sleep and power-saving mode -> readyState === ended (may get set, not necessary)
        // Ref - https://developer.mozilla.org/en-US/docs/Web/API/MediaStreamTrack || https://developer.mozilla.org/en-US/docs/Web/API/MediaStream/getVideoTracks
        // Check and let progress only if mediaTrack (camera) readyState === "live"

        const cameraFeed = RECORDING_CONST?.stream?.getVideoTracks()
        const checkCameraReadyState = cameraFeed.length > 0 && cameraFeed[0]?.readyState === "live"
        if (!checkCameraReadyState) {
            // Trigger permission modal -> Same as no internet type
            setIsPermissionModalVisible(PERMISSION_ERRORS.CAMERA_FEED);
            return;
        }
        const fileName = `video|${moment(currentTime).format("DD-MM-YYYY")}|${moment(currentTime).format("HH:mm:ss")}.${fileExtension}`
        const file = new File([event.data], fileName, {
            type: mimeType,
        })
        let currentVideoOrder = Number(storage.get.userVideoOrderCount())
        let videoSessionId = storage.get.userVideoSessionId()
        storage.set.userVideoOrderCount(currentVideoOrder + 5)
        try {
            await ProctoringApi.sendUserVideo({
                file,
                fileName,
                startTime: currentVideoOrder,
                endTime: currentVideoOrder + 5,
                duration: 5,
                sessionId: videoSessionId,
                type: "video",
                // [AUDIO PROCTORING v2] client wall-clock at chunk finalize, for
                // skew-proof audio↔video correlation (additive; offsets unchanged).
                recordedAt: currentTime.toISOString(),
            })
            apiFailureCountRef.current = 0
        } catch (err) {
            console.error(err.message)
            apiFailureCountRef.current += 1
            if (apiFailureCountRef.current >= 3) {
                setIsPermissionModalVisible(PERMISSION_ERRORS.API_FAILURE)
            }
        }

    }, [recorder])

    const startRecording = () => {
        try {
            if (recorder?.state !== "recording") {
                recorder?.start()
                setRecorderState(recorder?.state)
                clearTimeout(RECORDING_CONST.initialTimeout)
                RECORDING_CONST.initialTimeout = setTimeout(() => {
                    if (recorder?.state !== "inactive") {
                        recorder?.stop()
                        setRecorderState(recorder?.state)
                    }
                }, RECORDING_CONST.recordingInterval)
            }
        } catch {
            setIsPermissionModalVisible(true)
        }

    }

    const stopRecording = () => {
        try {
            if (recorder?.state !== "inactive") {
                recorder?.stop()
                setRecorderState("")
                clearTimeout(RECORDING_CONST.initialTimeout)
                RECORDING_CONST.stream?.getTracks()?.forEach(function (track) {
                    track?.stop()
                })
            }
        } catch {
            setIsPermissionModalVisible(true)
        }

    }

    return { isPermissionModalVisible }
}


const RECORDING_CONST = {
    stream: null,
    initialTimeout: "",
    recordingInterval: 6000,
    duration: 5,
    videoBitsPerSecond: 1000000,
    audioBitsPerSecond: 50000

}
